import { useCallback, useRef, useState } from 'react'
import { Button, Input, Select, Space, Spin, theme } from 'antd'
import type { InputRef } from 'antd'
import { DownloadOutlined, SendOutlined, FileOutlined } from '@ant-design/icons'
import { createUuid } from '@/utils/uuid'
import type { Msg , StockReport} from '@/types'
import { getStockQuestions } from '@/api'
import '@/styles/aiChat.scss'
import './AIChat.scss'
import { MdViewer } from '@/components/MarkdownFile'

const SKILLS = [
    { value: 'rag-qa', label: '研报问答' },
    { value: 'summarize-business', label: '公司财报提取分析' },
]

export default function AIChat({code, report }: { code: string, report: StockReport | null }) {
    const { token } = theme.useToken()
    const [sessionId] = useState(() => createUuid())
    const [messages, setMessages] = useState<Msg[]>([])
    const [input, setInput] = useState('')
    const [skillId, setSkillId] = useState('rag-qa')
    const [loading, setLoading] = useState(false)
    const [streamingAnswer, setStreamingAnswer] = useState('')
    const chatBodyRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<InputRef>(null)

    const scrollBottom = () =>
        setTimeout(() => {
            const chatBody = chatBodyRef.current
            if (!chatBody) return
            chatBody.scrollTo({
                top: chatBody.scrollHeight,
                behavior: 'smooth',
            })
        }, 50)

    const send = useCallback(async (question: string) => {
        if (!question || loading) return
        const userMsg: Msg = { session_id: sessionId, role: 'user', message: question, time: new Date().toLocaleTimeString() }
        setMessages((prev) => [...prev, userMsg])
        setInput('')
        setLoading(true)
        setStreamingAnswer('')
        scrollBottom()
        try {
            const isFinancialExtraction = skillId === 'summarize-business'
            const res = await getStockQuestions({
                code,
                question,
                skillId: isFinancialExtraction ? skillId : undefined,
                sourceFileId: isFinancialExtraction ? report?.id : undefined,
                onSectionStart: (section) => {
                    setStreamingAnswer((prev) => `${prev}${prev ? '\n\n' : ''}## ${section.title}\n\n`)
                    scrollBottom()
                },
                onSectionDelta: (delta) => {
                    setStreamingAnswer((prev) => `${prev}${delta.text}`)
                    scrollBottom()
                },
                onSectionComplete: (section) => {
                    setStreamingAnswer((prev) => `${prev}${prev ? '\n\n' : ''}${section.markdown}`)
                    scrollBottom()
                },
            })
            setMessages((prev) => [
                ...prev,
                { session_id: sessionId, role: 'assistant', message: res.answer, time: new Date().toLocaleTimeString() },
            ])
            scrollBottom()
        } catch (error) {
            const message = error instanceof Error ? error.message : '财报问答请求失败'
            setMessages((prev) => [
                ...prev,
                { session_id: sessionId, role: 'assistant', message, time: new Date().toLocaleTimeString() },
            ])
            scrollBottom()
        } finally {
            setLoading(false)
            setStreamingAnswer('')
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [code, loading, report?.id, sessionId, skillId])

    const handleClear = () => {
        setMessages([])
    }

    const downloadLatestAnswer = (extension: 'md' | 'txt') => {
        const answer = [...messages].reverse().find((message) => message.role === 'assistant')?.message
        if (!answer) return
        const blob = new Blob([answer], { type: extension === 'md' ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${code || 'financial-report-analysis'}.${extension}`
        link.click()
        URL.revokeObjectURL(url)
    }

    return (
        <div className='ai-chat-container'>
            {/* Header */}
            <div
                className='ai-chat-header'
            >
                <div className='ai-chat-header-title'>
                    <FileOutlined style={{ color: '#3b82f6' }} />研报问答<div className='ai-dot'></div>
                </div>
                <Space size={8}>
                    <Select
                        size="small"
                        value={skillId}
                        onChange={setSkillId}
                        options={SKILLS}
                        style={{ width: 168 }}
                        disabled={loading}
                    />
                    <Button size="small" icon={<DownloadOutlined />} disabled={!messages.some((message) => message.role === 'assistant')} onClick={() => downloadLatestAnswer('md')}>MD</Button>
                    <Button size="small" disabled={!messages.some((message) => message.role === 'assistant')} onClick={() => downloadLatestAnswer('txt')}>TXT</Button>
                    <Button size="small" className='text-10' onClick={handleClear}>清空</Button>
                </Space>
            </div>

            {/* Chat area */}
            <div className='ai-chat-body' ref={chatBodyRef}>
                <div className='ai-chat-body-content'>
                    {report && (
                        <section className='ai-chat-body-content-section'>
                            <div className='ai-chat-body-content-report-title'>
                                {report?.title}
                                <div className='text-10 color-3'>发布日期：{report?.published_date}</div>
                            </div>
                        </section>
                    )}

                    <section className='ai-chat-body-content-section ai-chat-body-content-section-instruction'>
                        <div className='ai-chat-body-content-title text-10 color-3'>快捷提问</div>
                        <div className='ai-chat-body-content-instruction-content flex items-center gap-8 flex-wrap' style={{ marginTop: 12, marginBottom: 12 }}>
                            {
                                ['核心逻辑是什么？','目标价调整的依据？','主要风险点？','与上篇报告有何变化？'].map((ele) => (
                                    <Button className='ai-chat-body-content-instruction-content-item text-11' onClick={() => send(ele)} style={{ color: 'var(--text-2)' }} key={ele}>
                                        {ele}
                                    </Button>
                                ))
                            }
                        </div>
                    </section>
                    {skillId === 'summarize-business' && !report && (
                        <section className='ai-chat-body-content-section ai-chat-body-content-section-instruction'>
                            <div className='text-10 color-3'>请先在“研究文档”中选择一份财报，再执行提取分析。</div>
                        </section>
                    )}
                </div>

                {
                    messages.map((msg, index) => (
                        <div key={index} className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}>
                            {msg.role === 'user' ? msg.message : <MdViewer content={msg.message} />}
                            <div className='ai-message-time'>{msg.time}</div>
                        </div>
                    ))
                }

                {streamingAnswer && (
                    <div className="ai-message ai-message-assistant">
                        <MdViewer content={streamingAnswer} />
                    </div>
                )}

                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                        <div style={{ padding: '8px 12px', background: token.colorFillSecondary, borderRadius: 8 }}>
                            <Spin size="small" />
                        </div>
                    </div>
                )}
            </div>

            {/* Input bar */}
            <div style={{ padding: '20px', borderTop: `1px solid var(--border)` }}>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPressEnter={() => send(input)}
                        placeholder={skillId === 'summarize-business' ? '输入分析重点，或直接发送以完整提取当前财报...' : '输入分析指令或问题...'}
                        disabled={loading || (skillId === 'summarize-business' && !report)}
                    />
                    <Button
                        type="primary"
                        style={{height: 36}}
                        icon={<SendOutlined />}
                        onClick={() => send(input || (skillId === 'summarize-business' ? '请按所选 Skill 对这份财报进行完整提取和分析。' : ''))}
                        loading={loading}
                        disabled={skillId === 'summarize-business' && !report}
                    />
                </Space.Compact>
            </div>
        </div>
    )
}
